"use client";
import { useCallback } from "react";
import {
  useCopilotDrawerState,
  useConversationId,
} from "@zapier/ai-toolkit/client";
import { AssetType, NavigationLocationType } from "@zapier/canvas-state";

import { updateUrlWithCopilotQueryParams } from "../utils/updateUrlWithCopilotQueryParams";
import { useEmitUserInteractionEvents } from "./useEmitUserInteractionEvents";

/**
 * Consolidated hook for opening assets in new tabs with copilot state sync.
 * Replaces the repetitive pattern of updateUrlWithCopilotQueryParams + window.open.
 *
 * TODO: Consider converting to atoms if needed from other atoms in the future.
 * Current hook approach works well for component usage patterns.
 */
export function useOpenAsset() {
  const [isCopilotOpen] = useCopilotDrawerState();
  const conversationId = useConversationId();
  const { emitEventOnAssetOpen } = useEmitUserInteractionEvents();

  return useCallback(
    ({
      url,
      assetType,
      location,
    }: {
      url: string | undefined | null;
      assetType: AssetType;
      location: NavigationLocationType;
    }) => {
      if (!url || !assetType) return;

      try {
        // Handle agent-specific URL transformation
        const processedUrl =
          assetType === "agent"
            ? url.replace("/settings-preview", "/settings")
            : url;

        // Resolve asset URLs with copilot state for cross-domain sync
        const updatedUrl = updateUrlWithCopilotQueryParams({
          url: processedUrl,
          assetType,
          isCopilotDrawerOpen: isCopilotOpen,
          conversationId,
        });

        // Emit analytics event
        if (assetType) {
          emitEventOnAssetOpen({
            asset: assetType,
            location,
          });
        }

        // Open in new tab
        window.open(updatedUrl, "_blank", "noopener,noreferrer");
      } catch (error) {
        console.error(`Failed to open ${assetType} URL:`, error);
      }
    },
    [emitEventOnAssetOpen, isCopilotOpen, conversationId],
  );
}
